import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Plus, Pencil, Eye, EyeOff, Power,
  AlertCircle, Tag, History, Save, AlertTriangle, Banknote, Star,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { PermissionGate } from '@/components/PermissionGate';
import { getPatrolDispatchFee, setPatrolDispatchFee } from '@/services/payments';

type PlanTier = 'TRIAL' | 'BASIC' | 'PRO' | 'PREMIUM';
type BillingPeriod = 'trial' | 'monthly' | 'annual';

interface PlanRow {
  id: string;
  code: string;
  /** null = plan personalizado, no mapea a un tier legado (TRIAL/BASIC/PRO/PREMIUM). */
  tier: PlanTier | null;
  name: string;
  description: string | null;
  price_amount: number;
  price_currency: string;
  billing_period: BillingPeriod;
  trial_days: number | null;
  features: Record<string, unknown>;
  member_limit: number | null;
  patrol_discount_pct: number;
  is_active: boolean;
  is_public: boolean;
  is_recommended: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PriceHistoryRow {
  id: string;
  plan_id: string;
  price_amount: number;
  price_currency: string;
  effective_from: string;
  changed_by: string | null;
  note: string | null;
}

const TIER_COLORS: Record<PlanTier, string> = {
  TRIAL:   'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  BASIC:   'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  PRO:     'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  PREMIUM: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
};
const CUSTOM_TIER_COLOR = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';

const BILLING_LABEL: Record<BillingPeriod, string> = {
  trial: 'Prueba', monthly: 'Mensual', annual: 'Anual',
};

const FEATURE_LABELS: Record<string, string> = {
  patrol_dispatch:  'Despacho de patrulla',
  patrol_limit:     'Límite de patrullas',
  cameras:          'Cámaras integradas',
  call_only:        'Solo llamada al ARC',
  panels:           'Paneles de alarma (ver)',
  arm_disarm:       'Armar / Desarmar paneles',
  arm_reminder:     'Recordatorio de armado',
  shake_sos:        'SOS por agitación y caída',
  geofences:        'Geocercas',
  camera_limit:     'Límite de cámaras',
  geofence_limit:     'Límite de geocercas',
  history_days:       'Días de historial',
  patrol_monthly_free: 'Patrullas gratuitas/mes',
};

// Catalog of known boolean features for the friendly editor.
const BOOL_FEATURES: { key: string; label: string; help: string }[] = [
  { key: 'patrol_dispatch', label: 'Despacho de patrulla',      help: 'El plan incluye envío de patrulla en SOS / PÁNICO.' },
  { key: 'call_only',       label: 'Solo llamada al ARC',       help: 'Marca el plan como "solo llamada" (sin despacho ni extras).' },
  { key: 'cameras',         label: 'Cámaras integradas',        help: 'Permite ver cámaras del sitio desde la app.' },
  { key: 'panels',          label: 'Paneles de alarma (ver)',   help: 'El cliente puede ver el estado de los paneles de alarma.' },
  { key: 'arm_disarm',      label: 'Armar / Desarmar paneles',  help: 'El cliente puede armar y desarmar sus paneles desde la app.' },
  { key: 'arm_reminder',    label: 'Recordatorio de armado',    help: 'Envía notificación si el sitio queda desarmado a cierta hora.' },
  { key: 'shake_sos',       label: 'SOS por agitación y caída', help: 'Activa SOS automáticamente al detectar agitación o caída.' },
  { key: 'geofences',       label: 'Geocercas',                 help: 'Alertas automáticas al entrar / salir de zonas definidas.' },
];

const EMPTY_FORM = {
  code: '',
  // null = plan personalizado (sin tier legado) — ya no es obligatorio
  // forzar el plan a TRIAL/BASIC/PRO/PREMIUM para crearlo.
  tier: null as PlanTier | null,
  name: '',
  description: '',
  price_amount: '0',
  price_currency: 'GTQ',
  billing_period: 'monthly' as BillingPeriod,
  trial_days: '',
  member_limit: '',
  patrol_discount_pct: '0',
  is_active: true,
  is_public: true,
  sort_order: '0',
  features: {} as Record<string, unknown>,
};

type Form = typeof EMPTY_FORM;

function formToInsert(f: Form) {
  return {
    code: f.code.trim().toUpperCase(),
    tier: f.tier,
    name: f.name.trim(),
    description: f.description.trim() || null,
    price_amount: Number(f.price_amount) || 0,
    price_currency: f.price_currency.trim().toUpperCase() || 'GTQ',
    billing_period: f.billing_period,
    trial_days: f.trial_days ? Number(f.trial_days) : null,
    member_limit: f.member_limit ? Number(f.member_limit) : null,
    patrol_discount_pct: Math.min(100, Math.max(0, Math.round(parseFloat(f.patrol_discount_pct || '0') * 100) / 100)),
    is_active: f.is_active,
    is_public: f.is_public,
    sort_order: Number(f.sort_order) || 0,
    features: f.features ?? {},
  };
}

function rowToForm(p: PlanRow): Form {
  return {
    code: p.code,
    tier: p.tier,
    name: p.name,
    description: p.description ?? '',
    price_amount: String(p.price_amount),
    price_currency: p.price_currency,
    billing_period: p.billing_period,
    trial_days: p.trial_days != null ? String(p.trial_days) : '',
    member_limit: p.member_limit != null ? String(p.member_limit) : '',
    patrol_discount_pct: String(p.patrol_discount_pct ?? 0),
    is_active: p.is_active,
    is_public: p.is_public,
    sort_order: String(p.sort_order),
    features: { ...(p.features ?? {}) },
  };
}

export function PlansPage() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [historyOpenFor, setHistoryOpenFor] = useState<PlanRow | null>(null);
  const [history, setHistory] = useState<PriceHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Patrol dispatch base fee
  const [patrolFee, setPatrolFee]           = useState<number>(39);
  const [patrolFeeInput, setPatrolFeeInput] = useState<string>('39');
  const [patrolFeeSaving, setPatrolFeeSaving] = useState(false);
  const [patrolFeeError, setPatrolFeeError]   = useState<string | null>(null);
  const [patrolFeeOk, setPatrolFeeOk]         = useState(false);

  useEffect(() => {
    getPatrolDispatchFee()
      .then((fee) => { setPatrolFee(fee); setPatrolFeeInput(String(fee)); })
      .catch(() => { /* keep default */ });
  }, []);

  const handleSavePatrolFee = async () => {
    const val = Math.round(parseFloat(patrolFeeInput.replace(',', '.')) * 100) / 100;
    if (!isFinite(val) || val <= 0) { setPatrolFeeError('Ingresa un monto válido mayor a 0'); return; }
    setPatrolFeeSaving(true); setPatrolFeeError(null); setPatrolFeeOk(false);
    try {
      await setPatrolDispatchFee(val);
      setPatrolFee(val); setPatrolFeeOk(true);
      setTimeout(() => setPatrolFeeOk(false), 3000);
    } catch (e) {
      setPatrolFeeError(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally { setPatrolFeeSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });
      if (e) throw e;
      setPlans((data ?? []) as PlanRow[]);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Error cargando planes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Realtime: auto-refresh on plan / price-history changes.
  useEffect(() => {
    const channel = supabase
      .channel(`plans-realtime-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscription_plans' }, () => { void load(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscription_plan_price_history' }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [load]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
  };

  const openEdit = (plan: PlanRow) => {
    setEditing(plan);
    setCreating(false);
    setForm(rowToForm(plan));
    setSaveError(null);
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY_FORM);
    setSaveError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = formToInsert(form);
      if (!payload.code) throw new Error('El código es obligatorio');
      if (!payload.name) throw new Error('El nombre es obligatorio');

      if (editing) {
        // Detect price change → insert price history row first.
        const previousPrice = Number(editing.price_amount);
        const { data: userRes } = await supabase.auth.getUser();
        if (payload.price_amount !== previousPrice) {
          const { error: hErr } = await supabase
            .from('subscription_plan_price_history')
            .insert({
              plan_id: editing.id,
              price_amount: payload.price_amount,
              price_currency: payload.price_currency,
              changed_by: userRes.user?.id ?? null,
              note: `Cambio manual desde ${previousPrice} → ${payload.price_amount}`,
            });
          if (hErr) throw hErr;
        }
        const { error: uErr } = await supabase
          .from('subscription_plans')
          .update(payload)
          .eq('id', editing.id);
        if (uErr) throw uErr;
      } else {
        const { data: inserted, error: iErr } = await supabase
          .from('subscription_plans')
          .insert(payload)
          .select('id')
          .single();
        if (iErr) throw iErr;
        // Snapshot initial price.
        const { data: userRes } = await supabase.auth.getUser();
        await supabase.from('subscription_plan_price_history').insert({
          plan_id: inserted!.id,
          price_amount: payload.price_amount,
          price_currency: payload.price_currency,
          changed_by: userRes.user?.id ?? null,
          note: 'Creación de plan',
        });
      }
      close();
      await load();
    } catch (e: unknown) {
      setSaveError((e as { message?: string })?.message ?? 'Error guardando el plan');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (plan: PlanRow, field: 'is_active' | 'is_public') => {
    const { error: e } = await supabase
      .from('subscription_plans')
      .update({ [field]: !plan[field] })
      .eq('id', plan.id);
    if (e) {
      alert(e.message);
      return;
    }
    await load();
  };

  /** Only one plan shows the "RECOMENDADO" badge in the mobile Paywall at a
   *  time — clear the previous one first, then set the new one. Clicking the
   *  button on the currently-recommended plan un-recommends it (no badge
   *  shown at all, Paywall falls back to its hardcoded 'PRO' default). */
  const setRecommended = async (plan: PlanRow) => {
    const makingRecommended = !plan.is_recommended;
    const { error: clearErr } = await supabase
      .from('subscription_plans')
      .update({ is_recommended: false })
      .eq('is_recommended', true);
    if (clearErr) { alert(clearErr.message); return; }
    if (makingRecommended) {
      const { error: setErr } = await supabase
        .from('subscription_plans')
        .update({ is_recommended: true })
        .eq('id', plan.id);
      if (setErr) { alert(setErr.message); return; }
    }
    await load();
  };

  const openHistory = async (plan: PlanRow) => {
    setHistoryOpenFor(plan);
    setHistoryLoading(true);
    setHistory([]);
    try {
      const { data, error: e } = await supabase
        .from('subscription_plan_price_history')
        .select('*')
        .eq('plan_id', plan.id)
        .order('effective_from', { ascending: false });
      if (e) throw e;
      setHistory((data ?? []) as PriceHistoryRow[]);
    } catch (e: unknown) {
      alert((e as { message?: string })?.message ?? 'Error cargando historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) =>
    `${currency === 'GTQ' ? 'Q' : currency + ' '}${amount.toFixed(2)}`;

  const featureBadges = (features: Record<string, unknown>) => {
    const entries = Object.entries(features ?? {}).filter(([k]) => k !== 'coverage_disclaimer' && k !== 'coverage_minutes');
    if (entries.length === 0) return <span className="text-xs text-muted-foreground">Sin features</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {entries.map(([k, v]) => {
          const label = FEATURE_LABELS[k] ?? k;
          const truthy = v === true || (typeof v === 'number' && v > 0);
          if (typeof v === 'boolean') {
            return (
              <Badge
                key={k}
                variant="outline"
                className={truthy ? 'border-emerald-500/40 text-emerald-600' : 'border-muted text-muted-foreground line-through'}
              >
                {label}
              </Badge>
            );
          }
          return (
            <Badge key={k} variant="outline" className="border-blue-500/40 text-blue-600">
              {label}: {String(v)}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <PermissionGate require="plans.view">
      <div className="container mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Tag className="w-6 h-6" />
              Planes de suscripción
            </h1>
            <p className="text-sm text-muted-foreground">
              Catálogo editable de planes ofrecidos a los clientes. Cambiar el precio guarda automáticamente
              una entrada en el historial. Se actualiza automáticamente en tiempo real.
            </p>
          </div>
          <div className="flex gap-2">
            <PermissionGate require="plans.manage">
              <Button size="sm" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo plan
              </Button>
            </PermissionGate>
          </div>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6 flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </CardContent>
          </Card>
        )}

        {/* ── Patrol dispatch base fee ── */}
        <PermissionGate require="plans.manage">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Banknote className="w-4 h-4 text-blue-500" />
                Tarifa base por despacho de patrulla
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Monto en GTQ cobrado a clientes <strong>TRIAL</strong> por cada SOS.
                Cada plan puede aplicar un % de descuento sobre este valor.
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground shrink-0">GTQ</span>
                <Input
                  type="number" min="1" step="0.01"
                  value={patrolFeeInput}
                  onChange={(e) => { setPatrolFeeInput(e.target.value); setPatrolFeeError(null); setPatrolFeeOk(false); }}
                  className="w-28"
                  disabled={patrolFeeSaving}
                />
                <Button size="sm" onClick={handleSavePatrolFee}
                  disabled={patrolFeeSaving || patrolFeeInput === String(patrolFee)}>
                  {patrolFeeSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  <span className="ml-1">Guardar</span>
                </Button>
                {patrolFeeOk && <span className="text-xs text-emerald-600">✓ Guardado</span>}
              </div>
              {patrolFeeError && (
                <p className="text-xs text-rose-600 flex items-center gap-1 mt-2">
                  <AlertTriangle className="w-3 h-3" />{patrolFeeError}
                </p>
              )}
            </CardContent>
          </Card>
        </PermissionGate>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Cargando planes...
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((plan) => (
              <Card key={plan.id} className={!plan.is_active ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className={plan.tier ? TIER_COLORS[plan.tier] : CUSTOM_TIER_COLOR}>{plan.code}</Badge>
                        {plan.is_recommended && (
                          <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/30">
                            <Star className="w-3 h-3 mr-1 fill-current" /> Recomendado
                          </Badge>
                        )}
                        {!plan.is_active && <Badge variant="outline">Inactivo</Badge>}
                        {!plan.is_public && <Badge variant="outline" className="border-amber-500/40 text-amber-600">Privado</Badge>}
                      </div>
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">
                        {formatPrice(Number(plan.price_amount), plan.price_currency)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {BILLING_LABEL[plan.billing_period]}
                        {plan.trial_days ? ` · ${plan.trial_days}d` : ''}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {plan.description && (
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  )}
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Features</div>
                    {featureBadges(plan.features ?? {})}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      Miembros máx: {plan.member_limit ?? '∞'} · orden #{plan.sort_order}
                    </span>
                    {Number(plan.patrol_discount_pct) > 0 && (
                      <Badge className="bg-blue-500/10 text-blue-600 border border-blue-500/30 text-[10px]">
                        {Number(plan.patrol_discount_pct)}% dto. patrulla
                      </Badge>
                    )}
                  </div>
                  <PermissionGate require="plans.manage">
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button size="sm" variant="outline" onClick={() => openEdit(plan)}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void toggle(plan, 'is_active')}>
                        <Power className="w-3.5 h-3.5 mr-1" />
                        {plan.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void toggle(plan, 'is_public')}>
                        {plan.is_public ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                        {plan.is_public ? 'Ocultar' : 'Publicar'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void setRecommended(plan)}>
                        <Star className="w-3.5 h-3.5 mr-1" />
                        {plan.is_recommended ? 'Quitar recomendado' : 'Marcar recomendado'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void openHistory(plan)}>
                        <History className="w-3.5 h-3.5 mr-1" /> Historial
                      </Button>
                    </div>
                  </PermissionGate>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit / create dialog */}
        <Dialog open={creating || editing != null} onOpenChange={(o) => { if (!o) close(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? `Editar plan ${editing.code}` : 'Nuevo plan'}</DialogTitle>
            </DialogHeader>
            <PlanFormEditor form={form} setForm={setForm} disableCode={!!editing} />
            {saveError && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="w-4 h-4" /> {saveError}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={saving}>Cancelar</Button>
              <Button onClick={() => void save()} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? 'Guardar cambios' : 'Crear plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Price history dialog */}
        <Dialog open={historyOpenFor != null} onOpenChange={(o) => { if (!o) { setHistoryOpenFor(null); setHistory([]); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Historial de precios — {historyOpenFor?.code}</DialogTitle>
            </DialogHeader>
            {historyLoading ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando...
              </div>
            ) : history.length === 0 ? (
              <div className="text-sm text-muted-foreground">Sin cambios registrados.</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="border rounded-md p-3 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-semibold">
                        {h.price_currency === 'GTQ' ? 'Q' : `${h.price_currency} `}
                        {Number(h.price_amount).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(h.effective_from).toLocaleString('es-GT')}
                      </div>
                      {h.note && <div className="text-xs text-muted-foreground italic">{h.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  );
}

function PlanFormEditor({
  form, setForm, disableCode,
}: { form: Form; setForm: (f: Form) => void; disableCode: boolean }) {
  const update = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm({ ...form, [key]: value });

  const setFeature = (key: string, value: unknown) => {
    const next = { ...(form.features ?? {}) };
    if (value === undefined || value === null || value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    update('features', next);
  };

  const patrolLimitValue = form.features?.patrol_limit;
  const patrolLimitStr =
    typeof patrolLimitValue === 'number' ? String(patrolLimitValue) : '';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold">Código</label>
          <Input
            value={form.code}
            onChange={(e) => update('code', e.target.value.toUpperCase())}
            disabled={disableCode}
            placeholder="PRO"
          />
        </div>
        <div>
          <label className="text-xs font-semibold">Tier</label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.tier ?? ''}
            onChange={(e) => update('tier', (e.target.value || null) as PlanTier | null)}
          >
            <option value="">Personalizado (sin tier)</option>
            <option value="TRIAL">TRIAL</option>
            <option value="BASIC">BASIC</option>
            <option value="PRO">PRO</option>
            <option value="PREMIUM">PREMIUM</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold">Nombre</label>
        <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Pro" />
      </div>
      <div>
        <label className="text-xs font-semibold">Descripción</label>
        <Input value={form.description} onChange={(e) => update('description', e.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold">Precio</label>
          <Input
            type="number" step="0.01" min="0"
            value={form.price_amount}
            onChange={(e) => update('price_amount', e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold">Moneda</label>
          <Input value={form.price_currency} onChange={(e) => update('price_currency', e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold">Periodo</label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.billing_period}
            onChange={(e) => update('billing_period', e.target.value as BillingPeriod)}
          >
            <option value="trial">Prueba</option>
            <option value="monthly">Mensual</option>
            <option value="annual">Anual</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold">Días de prueba</label>
          <Input
            type="number" min="0"
            value={form.trial_days}
            onChange={(e) => update('trial_days', e.target.value)}
            placeholder="(opcional)"
          />
        </div>
        <div>
          <label className="text-xs font-semibold">Miembros máx.</label>
          <Input
            type="number" min="0"
            value={form.member_limit}
            onChange={(e) => update('member_limit', e.target.value)}
            placeholder="(∞)"
          />
        </div>
        <div>
          <label className="text-xs font-semibold">Orden</label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => update('sort_order', e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update('is_active', e.target.checked)}
          />
          Activo
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_public}
            onChange={(e) => update('is_public', e.target.checked)}
          />
          Público
        </label>
      </div>
      <div className="space-y-2 rounded-md border p-3">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Características incluidas
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {BOOL_FEATURES.map((f) => (
            <label key={f.key} className="flex items-start gap-2 text-sm rounded-md border bg-background/50 p-2 cursor-pointer hover:bg-muted/40">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.features?.[f.key] === true}
                onChange={(e) => setFeature(f.key, e.target.checked ? true : false)}
              />
              <span>
                <span className="font-medium">{f.label}</span>
                <span className="block text-xs text-muted-foreground">{f.help}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="pt-2 border-t space-y-3">
          <div>            <label className="text-xs font-semibold">Descuento en despacho de patrulla (%)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0" max="100" step="0.01"
                className="max-w-[140px]"
                value={form.patrol_discount_pct}
                onChange={(e) => update('patrol_discount_pct', e.target.value)}
                placeholder="0"
              />
              <span className="text-xs text-muted-foreground">
                % de descuento sobre la tarifa base de patrulla configurada en Ajustes de Pagos.
                Ej. 20 = cobra el 80% del monto base. 0 = sin descuento.
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Patrullas sin costo por mes</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={
                  typeof form.features?.['patrol_monthly_free'] === 'number'
                    ? String(form.features['patrol_monthly_free'])
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('patrol_monthly_free', undefined);
                  else setFeature('patrol_monthly_free', Math.max(0, Number(v)));
                }}
                placeholder="ilimitadas"
              />
              <span className="text-xs text-muted-foreground">
                Despachos de patrulla gratuitos incluidos en el mes. Cada despacho adicional
                genera una factura al cliente (tarifa base con el % de descuento del plan).
                Vacío = todos gratuitos (sin cobro extra).
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Límite de patrullas por periodo (legado)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={patrolLimitStr}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('patrol_limit', undefined);
                  else setFeature('patrol_limit', Math.max(0, Number(v)));
                }}
                placeholder="sin límite"
              />
              <span className="text-xs text-muted-foreground">
                Solo usado en plan Trial (legado). Para planes activos usa «Patrullas sin costo por mes».
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Límite de cámaras</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={typeof form.features?.['camera_limit'] === 'number' ? String(form.features['camera_limit']) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('camera_limit', undefined);
                  else setFeature('camera_limit', Math.max(0, Number(v)));
                }}
                placeholder="sin límite"
              />
              <span className="text-xs text-muted-foreground">
                Máx. cámaras que puede registrar la cuenta. Vacío = ilimitado.
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Límite de geocercas</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={typeof form.features?.['geofence_limit'] === 'number' ? String(form.features['geofence_limit']) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('geofence_limit', undefined);
                  else setFeature('geofence_limit', Math.max(0, Number(v)));
                }}
                placeholder="sin límite"
              />
              <span className="text-xs text-muted-foreground">
                Máx. geocercas activas por cuenta.
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Días de historial de alertas</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={typeof form.features?.['history_days'] === 'number' ? String(form.features['history_days']) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('history_days', undefined);
                  else setFeature('history_days', Math.max(0, Number(v)));
                }}
                placeholder="ilimitado"
              />
              <span className="text-xs text-muted-foreground">
                Cuántos días de alertas puede consultar el cliente. Vacío = sin restricción.
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Minutos máx. de permanencia del patrullero en el sitio</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="0"
                className="max-w-[140px]"
                value={typeof form.features?.['coverage_minutes'] === 'number' ? String(form.features['coverage_minutes']) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setFeature('coverage_minutes', undefined);
                  else setFeature('coverage_minutes', Math.max(0, Number(v)));
                }}
                placeholder="45"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => {
                  const mins = typeof form.features?.['coverage_minutes'] === 'number' ? form.features['coverage_minutes'] : 45;
                  setFeature('coverage_disclaimer', `El patrullero puede permanecer hasta ${mins} minutos en el sitio. Cobertura disponible solo en la Ciudad de Guatemala, no incluye zonas rojas.`);
                }}
              >
                Generar texto
              </Button>
              <span className="text-xs text-muted-foreground">
                Tiempo que el patrullero puede quedarse en el sitio atendiendo la alerta (no es tiempo de traslado).
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Aviso de cobertura / tiempo de patrulla</label>
            <textarea
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring min-h-[72px]"
              value={typeof form.features?.['coverage_disclaimer'] === 'string' ? form.features['coverage_disclaimer'] : ''}
              onChange={(e) => setFeature('coverage_disclaimer', e.target.value || undefined)}
              placeholder="Ej: El patrullero puede permanecer hasta 45 minutos en el sitio. Cobertura disponible solo en la Ciudad de Guatemala, no incluye zonas rojas."
            />
            <span className="text-xs text-muted-foreground">
              Se muestra al cliente en la pantalla de suscripción (Paywall) para planes de solo app. Vacío = no se muestra. Edítalo libremente o usa "Generar texto" para la plantilla estándar con los minutos de arriba.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
